<template>
  <div class="task-stage-page">
    <!-- Documentation collection (auditor's view of the client's side) -->
    <section v-if="!client && selectedTask.data_collection_on" class="v2-field task-doc-card" data-qid="task-doc">
      <header class="v2-field__head">
        <h4>{{ $t('tasks.modal.data_collection_title') }}</h4>
        <span class="v2-tag" :class="docTag.cls">{{ docTag.text }}</span>
      </header>
      <dl class="task-facts">
        <dt>{{ $t('tasks.v2.client') }}</dt>
        <dd>
          <span v-for="user in selectedTask.data_users || []" :key="'data-user-' + user.id" class="task-facts__user">
            <Avatar :object="user" size="xs"></Avatar>{{ user.name }}
          </span>
          <span v-if="!(selectedTask.data_users || []).length" class="text-muted">{{ $t('risk.missing_input') }}</span>
          <span v-if="canEdit"
                class="v2-link cursor-pointer task-facts__add"
                @click="showReopenDataCollectionModal"
                data-qid="task-doc-data-users"
          >
            <font-awesome-icon icon="pen" class="mr-1"/>{{ $t('system.edit') }}
          </span>
        </dd>
        <template v-if="project.data_collection_end_date">
          <dt>{{ $t('tasks.v2.deadline') }}</dt>
          <dd>{{ project.data_collection_end_date | formatDate('DD MMM', true) }} <span class="text-muted">{{ $t('tasks.v2.project_date') }}</span></dd>
        </template>
        <template v-if="selectedTask.add_response_doc && selectedTask.add_response_doc !== 'No'">
          <dt>{{ $t('tasks.v2.response') }}</dt>
          <dd>
            <template v-if="editModeResponseDocMain && canEdit">
              <multiselect v-model="selectedAnswerDoc"
                           :options="allAnswersDoc"
                           label="title"
                           :multiple="false"
                           :placeholder="$t('tasks.modal.response_placeholder')"
                           :select-label="$t('system.dropdown_select_label')"
                           :selected-label="$t('system.dropdown_selected_label')"
                           :deselect-label="$t('system.dropdown_deselect_label')"
              ></multiselect>
              <div class="v2-field__actions">
                <b-button size="sm" variant="secondary" @click="discardResponseDoc(selectedTask)">{{ $t('tasks.modal.discard') }}</b-button>
                <b-button size="sm" variant="success" @click="updateResponseDoc(selectedTask)">{{ $t('tasks.modal.save') }}</b-button>
              </div>
            </template>
            <template v-else>
              <b v-if="selectedTask.response_doc">{{ selectedTask.response_doc }}</b>
              <span v-else class="text-muted">–</span>
              <span v-if="selectedTask.add_response_doc === 'Auditor' && canEdit"
                    class="v2-field__icon v2-field__icon--inline"
                    @click="editModeResponseDocMain = true"
              ><font-awesome-icon icon="pen"/></span>
            </template>
          </dd>
        </template>
        <dt>{{ $t('tasks.v2.comment') }}</dt>
        <dd>
          <span v-if="selectedTask.data_remarks" class="text-formatted">“<FormattedNoteText :value="selectedTask.data_remarks"/>”</span>
          <span v-else class="text-muted">–</span>
        </dd>
      </dl>
      <div v-if="isInOeTesting && canEdit" class="task-doc-card__note">
        <font-awesome-icon icon="lock" class="mr-2"/>{{ $t('tasks.v2.oe_client_note') }}
      </div>
      <div class="task-doc-card__foot">
        <span class="v2-link cursor-pointer" @click="switchCurrentView('data_collection')" data-qid="task-doc-open">
          <font-awesome-icon icon="external-link-alt" class="mr-1"/>{{ $t('tasks.modal.open_data_collection') }}
        </span>
        <span class="task-doc-card__buttons">
          <b-button v-if="canMoveBackToDi"
                    size="sm"
                    variant="secondary"
                    @click="backToDi"
                    data-qid="task-panel-back-to-di"
          ><font-awesome-icon icon="rotate-left" class="mr-1"/>{{ $t('tasks.v2.back_to_di') }}</b-button>
          <b-button v-if="selectedTask.data_status === 'completed' && canEdit"
                    size="sm"
                    variant="secondary"
                    @click="reopenFormSubmit"
                    data-qid="task-doc-reopen"
          >{{ $t('tasks.v2.reopen') }}</b-button>
        </span>
      </div>
    </section>
    <!-- Without a documentation collection the task moves back to D&I from here -->
    <span v-else-if="!client && canMoveBackToDi"
          class="task-di-back v2-link cursor-pointer"
          @click="backToDi"
          data-qid="task-panel-back-to-di"
    ><font-awesome-icon icon="rotate-left" class="mr-1"/>{{ $t('tasks.v2.back_to_di') }}</span>

    <!-- Attachments: the main task's files, or the client's files on the documentation collection -->
    <section class="v2-field v2-field--flush" data-qid="task-attachments" @dragover.prevent>
      <header class="v2-field__head">
        <h4>{{ client ? $t('tasks.v2.your_files') : $t('attachments.label') }}</h4>
        <span class="v2-field__meta">{{ files.length }}</span>
        <b-button v-if="canAddFiles"
                  size="sm"
                  variant="secondary"
                  @click="$refs.addAttachment.show(client ? 'data_collection' : 'main')"
                  :data-qid="client ? 'task-add-attachment-data-collection' : 'task-add-attachment-main'"
        >
          <font-awesome-icon icon="plus" class="mr-1"/>{{ $t('tasks.modal.add_attachment_label') }}
        </b-button>
      </header>
      <div v-for="item in files" :key="'file-' + item.id + '-' + item.linked" class="task-file">
        <font-awesome-icon :icon="opensAnnotated(item) ? 'highlighter' : 'paperclip'"
                           class="task-file__icon"
                           :class="{'task-file__icon--annotated': opensAnnotated(item)}"
        />
        <div class="task-file__text">
          <a :href="item.url"
             target="_blank"
             @click="viewFile(item, $event)"
             data-qid="download-media"
          >{{ item.full_title || item.file_name }}</a>
          <div class="task-file__sub" v-if="fileSub(item)">{{ fileSub(item) }}</div>
        </div>
        <span v-if="canAnnotateFile(item)"
              class="v2-field__icon"
              @click="annotateFile(item)"
              v-b-tooltip.hover
              :title="$t('annotations.annotate')"
              data-qid="annotate-media"
        ><font-awesome-icon icon="highlighter"/></span>
        <span class="v2-field__icon"
              @click="openOriginal(item)"
              v-b-tooltip.hover
              :title="$t('annotations.open_original')"
              data-qid="open-original-media"
        ><font-awesome-icon icon="eye"/></span>
        <span class="v2-field__icon"
              @click="downloadMedia(item)"
              v-b-tooltip.hover
              :title="$t('tasks.v2.download')"
              data-qid="download-media-button"
        ><font-awesome-icon icon="cloud-download-alt"/></span>
        <span v-if="canAddFiles"
              class="v2-field__icon v2-field__icon--danger"
              @click="item.linked ? openDeleteLinkedAttachmentModal(item) : openDeleteAttachmentModal(item)"
              v-b-tooltip.hover
              :title="$t('attachments.delete.title')"
              data-qid="remove-media-item"
        ><font-awesome-icon icon="trash-alt"/></span>
      </div>
      <div v-if="!files.length" class="v2-field__empty task-file__empty">{{ $t('tasks.v2.no_attachments') }}</div>
    </section>

    <AssessmentGlobalSingleTaskModalAddAttachment ref="addAttachment"></AssessmentGlobalSingleTaskModalAddAttachment>
    <DocumentAnnotationViewer ref="annotationViewer" @closed="loadAnnotationCounts"></DocumentAnnotationViewer>
  </div>
</template>

<script>
import piincHttp from '@/util/configureAxios.js';
import assessmentSingleTaskProxy from './assessmentSingleTaskProxy';
import Avatar from '@/components/Avatar.vue';
import AssessmentGlobalSingleTaskModalAddAttachment from './AssessmentGlobalSingleTaskModalAddAttachment.vue';
import DocumentAnnotationViewer from '@/components/annotations/DocumentAnnotationViewer.vue';
import { isAnnotatable } from '@/components/annotations/annotations.js';
import { openDocument as openDocumentInTab } from '@/util/openDocument.js';
import FormattedNoteText from '@/components/tasks/FormattedNoteText.vue';

// The documentation collection and the attachments of a task. On the main task (D&I page) it shows the
// client's side and all files; on the documentation collection (client) only the client's own files.
export default {
  name: 'EngagementTaskDocuments',
  mixins: [assessmentSingleTaskProxy],
  components: {
    FormattedNoteText,
    Avatar,
    AssessmentGlobalSingleTaskModalAddAttachment,
    DocumentAnnotationViewer
  },
  props: {
    client: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      // Annotations per file on this task (US 9350)
      annotationCounts: {}
    };
  },
  computed: {
    project() {
      return (this.selectedTask && this.selectedTask.project) || {};
    },
    canEdit() {
      return this.selectedTask && !this.readOnly;
    },
    // The main task can go back from OE testing to D&I testing
    canMoveBackToDi() {
      return !this.client && this.isInOeTesting && this.canChangeTestPhase && !this.readOnly;
    },
    // During OE testing the client only adds files in the tests; the files can still be read
    canAddFiles() {
      if (this.client) {
        return this.canEdit && this.selectedTask.data_status !== 'completed' && !this.dataCollectionLocked && !this.isInOeTesting;
      }
      return this.canEdit;
    },
    files() {
      const own = _.map(this.getMedia(this.client ? 'data_documents' : 'task_and_data_collection'), item => Object.assign({}, item, { linked: false }));
      const linked = _.map(this.selectedTask.linked_media || [], item => Object.assign({}, item, { linked: true }));
      return own.concat(linked);
    },
    docTag() {
      const task = this.selectedTask || {};
      if (this.isInOeTesting && task.status !== 'completed') {
        return { cls: 'v2-tag--blue', text: this.$t('tasks.v2.stage_oe_short') };
      }
      if (task.data_status === 'completed' || task.status === 'completed') {
        return { cls: 'v2-tag--violet', text: this.$t('tasks.v2.delivered') };
      }
      return { cls: 'v2-tag--amber', text: this.$t('tasks.v2.waiting_on_client') };
    }
  },
  methods: {
    // Back from OE testing to D&I testing; the samples stay on the OE tab
    backToDi() {
      if (!(this.tests || []).length) {
        this.setTestPhase(null);
        return;
      }
      this.$bvModal.msgBoxConfirm(this.$t('tasks.v2.back_to_di_confirm'), {
        title: this.$t('tasks.v2.back_to_di'),
        okTitle: this.$t('tasks.v2.back_to_di'),
        cancelTitle: this.$t('projects.create.cancel'),
        modalClass: 'v2-modal'
      }).then(confirmed => {
        if (confirmed) {
          this.setTestPhase(null);
        }
      });
    },
    fileSub(item) {
      let parts = [];
      if (this.annotationCounts[item.id]) {
        parts.push(this.$tc('tasks.v2.annotations_count', this.annotationCounts[item.id], { count: this.annotationCounts[item.id] }));
      }
      if (item.linked) {
        parts.push(this.$t('tasks.v2.evidence_library'));
      } else if (!this.client && item.collection_name === 'data_documents') {
        parts.push(this.$t('tasks.v2.from_client'));
      }
      return parts.join(' · ');
    },
    // Auditors annotate the main task's files (US 9350)
    canAnnotateFile(item) {
      return !this.client && this.isControlAssessment && isAnnotatable(item);
    },
    opensAnnotated(item) {
      return this.canAnnotateFile(item) && !!this.annotationCounts[item.id];
    },
    loadAnnotationCounts() {
      let self = this;
      const task = this.selectedTask;
      this.annotationCounts = {};
      if (this.client || !task || !task.id || !this.isControlAssessment || !task.project || !task.project.slug) {
        return;
      }
      piincHttp.get('projects/' + task.project.slug + '/annotations/counts', { params: { task_id: task.id } }).then(function(response) {
        if (self.selectedTask && self.selectedTask.id === task.id) {
          self.annotationCounts = response.data.counts || {};
        }
      }, function() {});
    },
    openOriginal(item) {
      openDocumentInTab(item);
    },
    // Annotated files open in the viewer with their annotations
    viewFile(item, event) {
      if (this.opensAnnotated(item)) {
        event.preventDefault();
        this.annotateFile(item);
      }
    },
    annotateFile(item) {
      this.$refs.annotationViewer.show({
        project: this.selectedTask.project,
        file: item,
        task: { id: this.selectedTask.id, title: this.selectedTask.full_title || this.selectedTask.title }
      });
    }
  },
  mounted() {
    this.$watch(() => {
      const task = this.selectedTask;
      return [task && task.id, task && task.project && task.project.slug].join('|');
    }, () => this.loadAnnotationCounts(), { immediate: true });
  }
};
</script>
